/**
 * Tokens of the reminder templates: {{token}} in the subject or the message.
 *
 * Each token is a path over the invoice of GET /invoices/{id}/. `fields` says
 * where it can be used (customer.name is message only: it can be too long for
 * a subject) and `kind` which templates offer it.
 *
 * Keep in sync with piriod-notifications
 * (src/notifications/reminders/render-message.js), which renders the
 * automatic reminders, and piriod-api (reminders/tokens.py), which validates
 * the collection templates.
 */
import moment from 'moment'

moment.locale('es')

export const REMINDER_TOKENS = [
  { value: 'number', label: 'Número de factura', type: String, kind: ['invoicing', 'collection'], fields: ['subject', 'message'] },
  { value: 'document.name', label: 'Tipo de factura', type: String, kind: ['invoicing', 'collection'], fields: ['subject', 'message'] },
  { value: 'date', label: 'Fecha de la factura', type: Date, kind: ['invoicing', 'collection'], fields: ['subject', 'message'] },
  { value: 'due_date', label: 'Fecha de vencimiento del pago', type: Date, kind: ['collection'], fields: ['subject', 'message'] },
  { value: 'total', label: 'Monto total', type: Number, kind: ['collection'], fields: ['subject', 'message'] },
  { value: 'payments[0].balance', label: 'Saldo pendiente', type: Number, kind: ['collection'], fields: ['subject', 'message'], fallback: 'total' },
  { value: 'customer.name', label: 'Nombre del cliente', type: String, kind: ['invoicing', 'collection'], fields: ['message'] }
]

const TOKEN_PATTERN = /{{\s*([^}]+?)\s*}}/

// Same output as piriod-notifications: integers with comma thousands.
const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

const defaultFormatNumber = value => numberFormat.format(value)

const defaultFormatDate = value => moment(value).format('DD MMM YYYY')

/**
 * Tokens offered in a template field.
 *
 * @param {string} field - 'subject' or 'message'.
 * @param {string} [kind='collection'] - Template kind.
 * @returns {Array}
 */
export const tokensForField = (field, kind = 'collection') =>
  REMINDER_TOKENS.filter(token => token.fields.includes(field) && token.kind.includes(kind))

/**
 * Value at a path like "customer.name" or "payments[0].balance", or undefined
 * when any level is missing.
 *
 * @param {Object} object
 * @param {string} path
 * @returns {*}
 */
export const resolvePath = (object, path) =>
  path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .reduce((value, key) => (value == null ? undefined : value[key]), object)

/**
 * Replace the {{tokens}} allowed in a template field with the invoice values,
 * as the automatic reminders do. Tokens without a value are left untouched.
 *
 * @param {string} text - Template subject or message.
 * @param {Object} invoice - Invoice as returned by GET /invoices/{id}/.
 * @param {Object} [options]
 * @param {string} [options.field='message'] - 'subject' or 'message'.
 * @param {Function} [options.formatNumber] - Amount formatter.
 * @param {Function} [options.formatDate] - Date formatter.
 * @returns {string}
 */
export const renderReminderTemplate = (text, invoice, { field = 'message', formatNumber = defaultFormatNumber, formatDate = defaultFormatDate } = {}) => {
  let rendered = text || ''

  REMINDER_TOKENS.filter(token => token.fields.includes(field)).forEach(token => {
    let property = resolvePath(invoice, token.value)
    if (property == null && token.fallback) {
      property = resolvePath(invoice, token.fallback)
    }

    if (property == null || property === '') {
      return
    }
    if (token.type === Date) {
      property = formatDate(property)
    }
    if (token.type === Number) {
      property = formatNumber(property)
    }
    rendered = rendered.split(`{{${token.value}}}`).join(property)
  })

  return rendered
}

/**
 * Tokens of `text` that are not allowed in the field, in order of appearance.
 *
 * @param {string} text
 * @param {string} field - 'subject' or 'message'.
 * @param {string} [kind='collection'] - Template kind.
 * @returns {Array<string>}
 */
export const invalidReminderTokens = (text, field, kind = 'collection') => {
  const allowed = tokensForField(field, kind).map(token => token.value)
  const tokens = (text || '').match(new RegExp(TOKEN_PATTERN.source, 'g')) || []
  return tokens.map(token => token.match(TOKEN_PATTERN)[1]).filter(token => !allowed.includes(token))
}

/**
 * Insert a {{token}} at the cursor of an input or textarea.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} input
 * @param {string} token - Token value, e.g. 'payments[0].balance'.
 * @returns {{ value: string, cursor: number }} New input value and the
 *   cursor position right after the inserted token.
 */
export const insertTokenAtCursor = (input, token) => {
  const value = input.value || ''
  const start = input.selectionStart == null ? value.length : input.selectionStart
  const end = input.selectionEnd == null ? start : input.selectionEnd
  const text = `{{${token}}}`

  return {
    value: value.slice(0, start) + text + value.slice(end),
    cursor: start + text.length
  }
}

/**
 * Sample invoice for the reminder previews, partially paid so that the total
 * and the pending balance differ.
 *
 * @param {string} [currency='CLP']
 * @returns {Object}
 */
export const previewInvoice = (currency = 'CLP') => ({
  id: null,
  customer: {
    name: 'Acme Inc'
  },
  currency,
  date: moment().subtract(20, 'days').format('YYYY-MM-DD'),
  document: {
    name: 'Factura electrónica'
  },
  due_date: moment().add(10, 'days').format('YYYY-MM-DD'),
  number: 17743,
  payments: [
    { amount: 1190000, balance: 590000, status: 'requires_payment_method' }
  ],
  total: 1190000
})
